import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { GetMultiProductsResponse, MarketplacePackagesApiService } from '@vendasta/marketplace-packages';
import { combineLatest, fromEvent as observableFromEvent, Observable, of, Subscription } from 'rxjs';
import { debounceTime, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { OmniSearchService } from './omni-search.service';

@Component({
  templateUrl: './account-group-searcher.component.html',
  styleUrls: ['./common.scss', './account-group-searcher.component.scss'],
  providers: [AccountGroupService],
})
export class AccountGroupSearcherComponent implements AfterViewInit, OnDestroy {
  accountGroups$: Observable<AccountGroup[]>;
  productIcons$: Observable<{ [key: string]: string }>;
  cachedProductIcons: { [key: string]: string } = {};
  cachedProductIds: Set<string> = new Set<string>();
  uniqueIdsSubscription: Subscription;

  @ViewChild('searchTerm', { static: true }) searchTerm: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<AccountGroupSearcherComponent>,
    private accountGroupService: AccountGroupService,
    private marketplaceAppService: MarketplacePackagesApiService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private router: Router,
    private omnisearchService: OmniSearchService,
  ) {}

  ngAfterViewInit(): void {
    const napPf = new ProjectionFilter({ accounts: true, napData: true });
    this.accountGroups$ = observableFromEvent(this.searchTerm.nativeElement, 'keyup').pipe(
      filter((e: KeyboardEvent) => {
        // Don't trigger a re-search on one of the non-letter keys
        return !['Meta', 'Alt', 'Shift', 'Control'].some((ignoredKey) => e.key === ignoredKey);
      }),
      debounceTime(300),
      map((_) => this.searchTerm.nativeElement.value),
      switchMap((searchTerm) => this.accountGroupService.lookup(napPf, [], searchTerm)),
      map((accountGroups) => {
        if (!accountGroups) {
          return [];
        }
        return accountGroups.map((ag) => {
          ag.accounts = ag.accounts.sort((a, b) => {
            // Custom products go to the back of the line
            if (a.marketplaceAppId.startsWith('CP')) {
              return 1;
            } else if (b.marketplaceAppId.startsWith('CP')) {
              return -1;
            }
            // Normal alphabetical sorting
            if (a.marketplaceAppId < b.marketplaceAppId) {
              return -1;
            } else if (a.marketplaceAppId > b.marketplaceAppId) {
              return 1;
            }
            return 0;
          });
          return ag;
        });
      }),
    );
    const uniqueProductIds$ = this.accountGroups$.pipe(
      map((accounts) => {
        if (!accounts) {
          return new Set();
        }
        const accountSet = new Set<string>();
        accounts.forEach((a) =>
          a.accounts.forEach((acc) => {
            accountSet.add(acc.marketplaceAppId);
          }),
        );
        return accountSet;
      }),
      shareReplay(1),
    );
    this.uniqueIdsSubscription = uniqueProductIds$.subscribe();
    this.productIcons$ = combineLatest([uniqueProductIds$, this.partnerId$]).pipe(
      switchMap(([accountIds, partnerId]) => {
        const ids = [];
        accountIds.forEach((a) => {
          ids.push(a);
        });
        if (ids.length === 0) {
          return of([]);
        }
        return this.marketplaceAppService.getMultiProducts({
          partnerId,
          productIds: ids,
          fieldMask: { paths: ['iconUrl', 'productId'] },
        });
      }),
      map((productContainers: GetMultiProductsResponse) => {
        if (!productContainers || !productContainers.products) {
          return this.cachedProductIcons;
        }
        productContainers.products.forEach((p) => this.cachedProductIds.add(p.product.productId));
        const idsToIcons = productContainers.products.reduce((pObj, product) => {
          pObj[product.product.productId] = product.product.iconUrl;
          return pObj;
        }, {});
        Object.keys(idsToIcons).forEach((key) => {
          this.cachedProductIcons[key] = idsToIcons[key];
        });
        return this.cachedProductIcons;
      }),
      startWith({}),
      shareReplay(1),
    );
  }

  ngOnDestroy(): void {
    this.uniqueIdsSubscription.unsubscribe();
  }

  navigateToLocation(accountGroupId: string): void {
    this.router.navigateByUrl(`/businesses/accounts/${accountGroupId}/details`);
    this.dialogRef.close();
  }
}
